import React, { useState, useEffect, useRef } from 'react';
import { CatalogGroupListItemDetails } from './CatalogGroupListItemDetails';
import { CatalogGroup } from '@/types';
import { deleteCatalogGroup } from '@/services/profile-catalog-groups';
import { ExpandableCard, CardHeader, ExpandableCardHandle, CardSection, CardDropdownMenu } from '@/components/Cards';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import { faEdit, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import theme from './CatalogGroupListItem.module.scss';
import { scrollTo } from '@/utils/html';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { CatalogGroupAddProductDialog } from './CatalogGroupAddProductDialog';
import { getProfileCatalogProductsByGroup } from '@/services/profile-catalog-products';
import { useAsyncState } from '@/utils/hooks';
import { Button, Dropdown } from '@ygtechnologies/rubus-ui';

interface CatalogGroupListItemProps {
    group: CatalogGroup;
    onEditClick?(group: CatalogGroup): void;
    onDelete?(group: CatalogGroup): void;
    defaultExpand?: boolean;
}

export const CatalogGroupListItem: React.FC<CatalogGroupListItemProps> = ({
    group,
    onEditClick,
    defaultExpand,
    onDelete,
}) => {
    const [expanded, setExpanded] = useState(!!defaultExpand);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddProductsDialog, setShowAddProductsDialog] = useState(false);
    const [showYesNoDialog, setShowYesNoDialog] = useState(false);

    const [products, setProducts, reloadProducts] = useAsyncState(
        () => getProfileCatalogProductsByGroup(group.id),
        [group.id],
        null
    );

    useEffect(() => {
        const load = async () => {
            await reloadProducts();
            setIsLoading(false);
        };
        load();
    }, [group, reloadProducts]);

    useEffect(() => {
        if (defaultExpand && !isLoading) {
            scrollTo(0, containerRef.current!.offsetTop);
        }
    }, [containerRef, isLoading, defaultExpand]);

    const handleDelete = async () => {
        await deleteCatalogGroup(group.id);
        triggerHandler(onDelete, group);
    };

    return (
        <div ref={containerRef}>
            <ExpandableCard
                expanded={expanded}
                expandableContent={
                    <CatalogGroupListItemDetails
                        products={products}
                        onProductsChange={(products) => setProducts(products)}
                        setIsLoading={setIsLoading}
                        group={group}
                    />
                }>
                <CardHeader>
                    <small>
                        {!!group.itemCategoryId ? (
                            <ItemCategoryDisplay value={group.itemCategoryId} />
                        ) : (
                            <Trans>No category</Trans>
                        )}
                    </small>

                    {group.name}
                </CardHeader>

                <CardSection className={theme.buttons} right>
                    <Button variant="tertiary" size="sm" icon={faEdit} onClick={() => setShowAddProductsDialog(true)}>
                        <Trans>Add products</Trans>
                    </Button>
                </CardSection>

                <ExpandableCardHandle
                    onExpandToggle={() => setExpanded(!expanded)}
                    expanded={expanded}
                    expandText={<Trans>Show products</Trans>}
                    collapseText={<Trans>Hide products</Trans>}
                />
                <CardDropdownMenu>
                    <Dropdown.Item
                        icon={faEdit}
                        onClick={
                            onEditClick
                                ? () => {
                                      onEditClick(group);
                                  }
                                : undefined
                        }>
                        <Trans>Edit product group</Trans>
                    </Dropdown.Item>
                    <Dropdown.Confirm
                        icon={faTrashAlt}
                        dialogTitle={<Trans>Delete product group</Trans>}
                        dialogBody={
                            <Trans>
                                Are you sure you want to delete this product group?
                                <br />
                                Linked groups and products will not be affected
                            </Trans>
                        }
                        onConfirm={handleDelete}>
                        <Trans>Delete product group</Trans>
                    </Dropdown.Confirm>
                </CardDropdownMenu>
            </ExpandableCard>
            <CatalogGroupAddProductDialog
                show={showAddProductsDialog}
                group={group}
                onClose={() => setShowAddProductsDialog(false)}
                onSave={() => reloadProducts()}
            />
        </div>
    );
};
